import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FormResponse } from '../../../lib';
import { ValidationErrors } from '../../../lib/models/validation/validation-errors';
import { EditableAppFlagsDto } from '../../mortgage-application/models/editable-app-flags-dto.model';
import { FormApplication } from '../../shared/models/form-application.model';
import { MortgageClubsDto } from '../../shared/models/mortgage-clubs-dto.model';
import { Kfi } from '../models/kfi.model';

@Injectable()
export class KfiService {
  constructor(protected readonly httpClient: HttpClient) {}

  private static getKfiResponse(data: any): FormResponse<Kfi> {
    const kfiResponse = new FormResponse<Kfi>();
    kfiResponse.Entity = new Kfi().deserialize(data.Entity);
    kfiResponse.Sections = data.Sections;
    kfiResponse.ValidationErrors = new ValidationErrors(data.ValidationErrors);

    return kfiResponse;
  }

  public loadKfi(kfiId: string): Observable<FormResponse<Kfi>> {
    const params = new HttpParams().set('kfiNumber', kfiId);

    return this.httpClient.get('/kfi/get', { params }).pipe(
      map((data: any) => {
        return KfiService.getKfiResponse(data);
      })
    );
  }

  public saveKfi(kfi: Kfi): Observable<FormResponse<Kfi>> {
    return this.httpClient.put('/kfi/update', kfi).pipe(
      map((data: any) => {
        return KfiService.getKfiResponse(data);
      })
    );
  }

  public submitKfi(kfi: Kfi): Observable<FormResponse<Kfi>> {
    return this.httpClient.put('/kfi/submit', kfi).pipe(
      map((data: any) => {
        return KfiService.getKfiResponse(data);
      })
    );
  }

  public loadAvailableLoanTypes(kfi: FormApplication): Observable<any> {
    const params = new HttpParams()
      .set('caseNumber', kfi.ApplicationCase.CaseNumberAsHash)
      .append('loanGroupTypeCode', kfi.ApplicationCase.LoanGroupTypeCode)
      .append('IsKfi', 'true')
      .append('IsAip', 'false')
      .append('IsFullApp', 'false')
      .append('propositionId', kfi.ApplicationCase.PropositionIDCache.toString());

    return this.httpClient.get('/ApplicationCase/GetLoanTypesByLoanGroupTypeCode', { params }).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public getMortgageClubs(application: FormApplication): Observable<MortgageClubsDto> {
    const params = new HttpParams().set('agentId', application.ApplicationCase.AgentID.toString());

    return this.httpClient.get('/InternalAgents/mortgageClubs', { params }).pipe(
      map((data: any) => {
        return data as MortgageClubsDto;
      })
    );
  }

  public isEditableApplication(kfiNumber: string): Observable<EditableAppFlagsDto> {
    return this.httpClient
      .get<EditableAppFlagsDto>('/kfi/IsEditableApplication', {
        params: {
          kfiNumber
        }
      })
      .pipe(
        map((response) => {
          return new EditableAppFlagsDto().deserialize(response);
        })
      );
  }

  public isDprServiceEnabled(): Observable<boolean | undefined> {
    return this.httpClient.get<boolean>('/kfi/IsDprServiceEnabled').pipe(
      map((response) => {
        return response as boolean;
      })
    );
  }

  public prepareData(kfi: Kfi): Kfi {
    if (!(this.propertyValuesCount(kfi.CaseProducts[0]) > 0)) {
      return { ...kfi, CaseProducts: undefined } as any;
    }

    return kfi;
  }

  private propertyValuesCount(object: Object): number {
    return Object.keys(object)
      .map((key) => object[key])
      .filter((value) => !!value && !this.isEmptyArray(value)).length;
  }

  private isEmptyArray(obj: Object): boolean {
    return Array.isArray(obj) && obj.length === 0;
  }
}
